import React, { useMemo, useState, useCallback } from "react"
import { toast } from "react-toastify"
import * as Yup from "yup"
import { useFormik } from "formik"
import {
  Col,
  Row,
  Modal,
  ModalHeader,
  ModalBody,
  Form,
  Input,
  FormFeedback,
  Label,
  Card,
  CardBody,
  Button,
  UncontrolledTooltip,
} from "reactstrap"

// Components
import TableContainer from "components/Common/TableContainer"
import Breadcrumbs from "components/Common/Breadcrumb"
import DeleteModal from "components/Common/DeleteModal"
import GlobalInputSearch from "components/Common/GlobalInputSearch"

// Hooks
import {
  useEngineTypes,
  useCreateEngineType,
  useEditEngineType,
  useDeleteEngineType,
} from "hooks/engineTypeHook"
import { useListParams } from "hooks/useListParams"

// Utils
import { formatReadableDate } from "utils/formatDate"

const EngineTypeList = () => {
  document.title = "Engine Types"

  const [modal, setModal] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [engineType, setEngineType] = useState(null)
  const [deleteModal, setDeleteModal] = useState(false)

  const { params, setPage, setSearch, resetParams, hasActiveFilters } =
    useListParams({
      page: 1,
      search: "",
    })

  const queryParams = useMemo(
    () => ({
      page: params.page,
      per_page: 10,
      ...(params.search && { search: params.search }),
    }),
    [params.page, params.search]
  )

  const {
    data: engineTypesResponse,
    isLoading,
    refetch: refetchEngineTypes,
    isRefetching,
  } = useEngineTypes(queryParams)

  const { mutateAsync: createEngineType, isPending: createLoading } =
    useCreateEngineType()
  const { mutateAsync: editEngineType, isPending: editLoading } =
    useEditEngineType()
  const { mutateAsync: deleteEngineType, isPending: deleteLoading } =
    useDeleteEngineType()

  const toggle = useCallback(() => {
    if (modal) {
      setModal(false)
      setEngineType(null)
      setIsEdit(false)
    } else setModal(true)
  }, [modal])

  const handleAddNewClick = useCallback(() => {
    setEngineType(null)
    setIsEdit(false)
    toggle()
  }, [toggle])

  const handleEditClick = useCallback(
    (data) => {
      setEngineType({ id: data.id, name: data.name })
      setIsEdit(true)
      toggle()
    },
    [toggle]
  )

  const handleDeleteClick = useCallback((data) => {
    setEngineType(data)
    setDeleteModal(true)
  }, [])

  const handleDelete = useCallback(async () => {
    if (engineType?.id) {
      try {
        const res = await deleteEngineType(engineType.id)
        toast.success(res?.message ?? "Engine type deleted successfully.")
        setDeleteModal(false)
      } catch (error) {
        toast.error("Failed to delete engine type.")
      }
    }
  }, [engineType, deleteEngineType])

  const handleRefresh = () => refetchEngineTypes()

  const validationSchema = Yup.object({
    name: Yup.string()
      .trim()
      .min(2, "Name must be at least 2 characters long")
      .required("Please enter engine type name"),
  })

  const onSubmit = useCallback(
    async (values, { resetForm }) => {
      const payload = { name: values.name }
      const action = isEdit
        ? editEngineType({ id: engineType.id, ...payload })
        : createEngineType(payload)

      try {
        const res = await action
        toast.success(
          res?.message ?? `Engine type ${isEdit ? "updated" : "created"} successfully.`
        )
        resetForm()
        toggle()
      } catch (error) {
        toast.error("Failed to save engine type.")
      }
    },
    [isEdit, engineType, createEngineType, editEngineType, toggle]
  )

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: engineType?.name || "",
    },
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit,
  })

  const columns = useMemo(
    () => [
      {
        header: "ID",
        accessorKey: "id",
      },
      {
        header: "Name",
        accessorKey: "name",
      },
      {
        header: "Slug",
        accessorKey: "slug",
      },
      {
        header: "Created",
        accessorKey: "created_at",
        cell: ({ row }) =>
          row.original.created_at ? formatReadableDate(row.original.created_at) : "-",
      },
      {
        header: "Action",
        cell: ({ row }) => (
          <ul className="list-unstyled hstack gap-1 mb-0">
            <li>
              <button
                className="btn btn-sm btn-soft-info"
                onClick={() => handleEditClick(row.original)}
              >
                <i className="mdi mdi-pencil-outline" />
              </button>
            </li>
            <li>
              <button
                className="btn btn-sm btn-soft-danger"
                onClick={() => handleDeleteClick(row.original)}
              >
                <i className="mdi mdi-delete-outline" />
              </button>
            </li>
          </ul>
        ),
      },
    ],
    [handleEditClick, handleDeleteClick]
  )

  const data = engineTypesResponse?.data?.data || []
  const paginationInfo = engineTypesResponse?.data

  return (
    <React.Fragment>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDelete}
        loading={deleteLoading}
        onCloseClick={() => setDeleteModal(false)}
      />
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="Engine Type" breadcrumbItem="Engine Types" />
          <Row>
            <Col lg="12">
              <Card>
                <CardBody className="border-bottom">
                  <div className="d-flex align-items-center">
                    <h5 className="mb-0 card-title flex-grow-1">Engine Types</h5>
                    <div className="flex-shrink-0">
                      <Button color="primary" onClick={handleAddNewClick} className="me-1">
                        Add New Engine Type
                      </Button>
                      <Button
                        color="light"
                        onClick={handleRefresh}
                        className="me-1"
                      >
                        <i className={`mdi mdi-refresh ${isRefetching ? "mdi-spin" : ""}`} />
                      </Button>
                      {hasActiveFilters && (
                        <Button color="danger" onClick={resetParams} className="me-1">
                          Reset Filters
                        </Button>
                      )}
                    </div>
                  </div>
                </CardBody>
                <CardBody>
                  <Row className="mb-3">
                    <Col sm={4}>
                      <GlobalInputSearch value={params.search} onChange={setSearch} />
                    </Col>
                  </Row>
                  <TableContainer
                    columns={columns}
                    data={data}
                    isPagination={true}
                    paginationInfo={paginationInfo}
                    onPageChange={setPage}
                    loading={isLoading || isRefetching}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Modal isOpen={modal} toggle={toggle} centered>
            <ModalHeader toggle={toggle} tag="h4">
              {isEdit ? "Edit Engine Type" : "Add Engine Type"}
            </ModalHeader>
            <ModalBody>
              <Form
                onSubmit={(e) => {
                  e.preventDefault()
                  formik.handleSubmit()
                }}
              >
                <div className="mb-3">
                  <Label htmlFor="name">Engine Type Name</Label>
                  <Input
                    name="name"
                    id="name"
                    type="text"
                    placeholder="Enter engine type name"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.name || ""}
                    invalid={formik.touched.name && !!formik.errors.name}
                  />
                  {formik.touched.name && formik.errors.name && (
                    <FormFeedback type="invalid">{formik.errors.name}</FormFeedback>
                  )}
                </div>
                <div className="text-end">
                  <Button color="success" type="submit" disabled={createLoading || editLoading}>
                    {isEdit ? "Save Changes" : "Add Engine Type"}
                    {(createLoading || editLoading) && "..."}
                  </Button>
                </div>
              </Form>
            </ModalBody>
          </Modal>
        </div>
      </div>
    </React.Fragment>
  )
}

export default EngineTypeList
